import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Check,
  Sparkles,
  Workflow,
  Cog,
  TrendingUp,
} from "lucide-react";
import {
  PACKAGES,
  OFFERING_PREVIEWS,
  SIGNATURE_PROGRAMS,
  TESTIMONIALS,
  FAQS,
  HERO_IMAGE,
  TABLET_IMAGE,
  ABSTRACT_TECH,
} from "@/lib/content";
import { trackEvent } from "@/lib/analytics";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import FinalCTA from "@/components/FinalCTA";

const SOLUTION_STEPS = [
  { label: "Map It", icon: Workflow, desc: "Identify what's slowing your business down." },
  { label: "Build It", icon: Cog, desc: "Create workflows, systems, and custom apps." },
  { label: "Automate It", icon: Sparkles, desc: "Connect AI and automation to your real operations." },
  { label: "Grow It", icon: TrendingUp, desc: "Move from scattered to scalable." },
];

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative bg-[var(--tmg-ivory)] overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 pt-16 pb-20 sm:pt-24 sm:pb-28 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="tmg-overline mb-6 tmg-fade-up">
                — Leadership · Workforce · Business Systems
              </p>
              <h1
                data-testid="hero-headline"
                className="font-display text-[2.5rem] sm:text-5xl lg:text-[4.2rem] font-semibold leading-[1.02] tracking-[-0.025em] tmg-fade-up-d1"
              >
                Stronger leaders. Stronger workforces.{" "}
                <span className="italic font-light text-[var(--tmg-gold-soft)]">
                  Systems that hold.
                </span>
              </h1>
              <p className="mt-8 text-base sm:text-lg text-[var(--tmg-muted)] max-w-xl leading-relaxed tmg-fade-up-d2">
                We help employers, communities, and growing businesses develop
                their people, build financial stability, and organize their
                operations in ways that last.
              </p>
              <div className="mt-10 flex flex-wrap gap-4 tmg-fade-up-d3">
                <Link
                  to="/book-call"
                  data-testid="hero-book-call-btn"
                  className="tmg-btn-primary"
                >
                  Book a discovery call <ArrowUpRight className="w-4 h-4" />
                </Link>
                <a
                  href="#signature-programs"
                  data-testid="hero-programs-btn"
                  className="tmg-btn-secondary"
                >
                  See our programs
                </a>
              </div>

              {/* Mini stats */}
              <div className="mt-14 grid grid-cols-3 gap-6 max-w-md tmg-fade-up-d4">
                {[
                  { k: "5+", v: "Service Pathways" },
                  { k: "$297", v: "Audit Start" },
                  { k: "1:1", v: "Personal Strategy" },
                ].map((s) => (
                  <div key={s.v} className="border-l border-[var(--tmg-line)] pl-4">
                    <div className="font-display text-3xl font-semibold text-[var(--tmg-obsidian)]">
                      {s.k}
                    </div>
                    <div className="tmg-overline mt-1">{s.v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 relative tmg-fade-up-d2">
              <div className="relative aspect-[4/5] max-w-md mx-auto">
                <div
                  aria-hidden
                  className="absolute -inset-4 border border-[var(--tmg-gold)]/40"
                />
                <div
                  aria-hidden
                  className="absolute -bottom-6 -right-6 w-32 h-32 bg-[var(--tmg-gold)]"
                />
                <img
                  src={HERO_IMAGE}
                  alt="TMG Consulting Group — principal consultant"
                  className="relative w-full h-full object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute bottom-6 left-6 right-6 bg-[var(--tmg-obsidian)] text-[var(--tmg-ivory)] p-5">
                  <p className="tmg-overline-gold mb-1">Meet</p>
                  <p className="font-display text-xl font-semibold">Our Principal Consultant</p>
                  <p className="text-xs text-white/60 mt-1 font-mono-sm">
                    TMG Consulting Group
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TWO AUDIENCE PATHS */}
      <section data-testid="audience-paths-section" className="bg-[var(--tmg-ivory)] border-b border-[var(--tmg-line)]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 pb-16 sm:pb-20">
          <div className="grid lg:grid-cols-2 gap-px bg-[var(--tmg-line)] border border-[var(--tmg-line)]">
            <div
              data-testid="audience-organizations"
              className="bg-white p-8 sm:p-12 hover:bg-[var(--tmg-ivory)] transition-all duration-300"
            >
              <p className="tmg-overline mb-5">— For Organizations</p>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-tight mb-5">
                Build leaders and a workforce that stays.
              </h2>
              <p className="text-base text-[var(--tmg-muted)] leading-relaxed mb-8">
                Leadership development, workforce retention, and financial
                empowerment programs for employers, health systems, nonprofits,
                and government entities.
              </p>
              <Link
                to="/book-call"
                data-testid="audience-org-cta"
                className="inline-flex items-center gap-2 text-sm font-semibold border-b border-[var(--tmg-obsidian)] pb-1 hover:border-[var(--tmg-gold)] hover:text-[var(--tmg-gold-soft)] transition-colors"
              >
                Book a discovery call <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
            <div
              data-testid="audience-small-business"
              className="bg-white p-8 sm:p-12 hover:bg-[var(--tmg-ivory)] transition-all duration-300"
            >
              <p className="tmg-overline mb-5">— For Small Businesses &amp; Entrepreneurs</p>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-tight mb-5">
                Stop running everything yourself.
              </h2>
              <p className="text-base text-[var(--tmg-muted)] leading-relaxed mb-8">
                Clear systems, strategy, and leadership support for owners ready
                to grow without burning out.
              </p>
              <Link
                to="/offerings"
                data-testid="audience-smb-cta"
                className="inline-flex items-center gap-2 text-sm font-semibold border-b border-[var(--tmg-obsidian)] pb-1 hover:border-[var(--tmg-gold)] hover:text-[var(--tmg-gold-soft)] transition-colors"
              >
                See what we do <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-b border-[var(--tmg-line)] bg-[var(--tmg-ivory-2)]/60">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 py-6 flex flex-wrap lg:flex-nowrap items-center justify-center gap-x-5 gap-y-2 text-[10px] text-[var(--tmg-muted)] font-mono-sm uppercase tracking-[0.1em] whitespace-nowrap">
          <span>Leadership Development</span>
          <span className="text-[var(--tmg-gold)]">·</span>
          <span>Workforce &amp; Financial Empowerment</span>
          <span className="text-[var(--tmg-gold)]">·</span>
          <span>Business Systems &amp; Operations</span>
          <span className="text-[var(--tmg-gold)]">·</span>
          <span>Culture &amp; Professional Development</span>
          <span className="text-[var(--tmg-gold)]">·</span>
          <span>Nonprofits</span>
        </div>
      </section>

      {/* PROBLEM */}
      <section data-testid="problem-section" className="tmg-section">
        <div className="tmg-container grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="tmg-overline mb-5">— The Problem</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
              Your business is growing,{" "}
              <span className="italic font-light text-[var(--tmg-gold-soft)]">
                but your systems may not be keeping up.
              </span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-12 border-l border-[var(--tmg-line)]">
            <p className="text-base sm:text-lg text-[var(--tmg-muted)] leading-relaxed">
              You have the ideas, the services, and the vision — but your systems
              may feel scattered. You may be using too many tools, doing too much
              manually, missing follow-ups, or hearing about AI without knowing
              how it actually applies to your business.
            </p>
            <p className="mt-6 text-base sm:text-lg text-[var(--tmg-obsidian)] leading-relaxed font-medium">
              This is where strategy, structure, and technology come together.
            </p>
            <p className="mt-6 text-base sm:text-lg text-[var(--tmg-muted)] leading-relaxed">
              We help you take the overwhelm out of business operations
              and turn your ideas into clear workflows, organized systems, and
              practical tools you can actually use.
            </p>
          </div>
        </div>
      </section>

      {/* SOLUTION — 5 step beam */}
      <section
        data-testid="solution-section"
        className="tmg-section bg-[var(--tmg-ivory-2)]/40 border-y border-[var(--tmg-line)]"
      >
        <div className="tmg-container">
          <div className="max-w-3xl mb-16">
            <p className="tmg-overline mb-5">— The Approach</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
              Map it. Build it. Automate it. Grow it.
            </h2>
            <p className="mt-6 text-base sm:text-lg text-[var(--tmg-muted)] leading-relaxed">
              The goal is not to use AI just because it is popular. The goal is
              to use the right tools, workflows, and systems to support the
              business you are building.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--tmg-line)]">
            {SOLUTION_STEPS.map((s, i) => (
              <div
                key={s.label}
                className="bg-white p-8 hover:bg-[var(--tmg-obsidian)] hover:text-white transition-all duration-500 group"
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="font-mono-sm text-xs text-[var(--tmg-gold)]">
                    0{i + 1}
                  </span>
                  <s.icon className="w-5 h-5 stroke-[1.5] group-hover:text-[var(--tmg-gold)]" />
                </div>
                <h3 className="font-display text-xl font-semibold mb-3">
                  {s.label}
                </h3>
                <p className="text-sm leading-relaxed text-[var(--tmg-muted)] group-hover:text-white/70">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="services-section" className="tmg-section">
        <div className="tmg-container">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-6">
            <div>
              <p className="tmg-overline mb-5">— What We Do</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight max-w-2xl">
                Four offerings. One goal: systems that hold.
              </h2>
            </div>
            <Link to="/offerings" className="tmg-btn-ghost" data-testid="all-services-link">
              Explore our offerings <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-[var(--tmg-line)]">
            {OFFERING_PREVIEWS.map((svc, i) => (
              <div
                key={svc.slug}
                data-testid={`service-card-${svc.slug}`}
                className="bg-white p-8 sm:p-10 transition-all duration-300 hover:bg-[var(--tmg-ivory)] group"
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="w-12 h-12 bg-[var(--tmg-ivory-2)] flex items-center justify-center group-hover:bg-[var(--tmg-obsidian)] group-hover:text-[var(--tmg-gold)] transition-all">
                    <svc.icon className="w-5 h-5 stroke-[1.5]" />
                  </span>
                  <span className="font-mono-sm text-xs text-[var(--tmg-muted)]">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display text-2xl font-semibold mb-3 leading-tight">
                  {svc.title}
                </h3>
                <p className="text-sm text-[var(--tmg-muted)] leading-relaxed mb-8 min-h-[4rem]">
                  {svc.sentence}
                </p>
                <Link
                  to={svc.to}
                  data-testid={`service-cta-${svc.slug}`}
                  className="inline-flex items-center gap-2 text-sm font-semibold border-b border-[var(--tmg-obsidian)] pb-1 hover:border-[var(--tmg-gold)] hover:text-[var(--tmg-gold-soft)] transition-colors"
                >
                  Learn more <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section
        data-testid="results-section"
        className="tmg-section bg-[var(--tmg-ivory-2)]/40 border-y border-[var(--tmg-line)]"
      >
        <div className="tmg-container grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <p className="tmg-overline mb-5">— Results</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
              Results that last
            </h2>
            <p className="mt-6 text-base sm:text-lg text-[var(--tmg-obsidian)] font-medium leading-relaxed">
              We measure what matters: retention, advancement, savings, and
              wellbeing.
            </p>
          </div>
          <div className="lg:col-span-7 lg:pl-12 border-l border-[var(--tmg-line)]">
            <div className="font-display text-7xl sm:text-8xl font-semibold text-[var(--tmg-gold)] leading-none mb-6">
              89%
            </div>
            <p className="text-base sm:text-lg text-[var(--tmg-muted)] leading-relaxed">
              In a community poverty-alleviation program led by our principal
              consultant, 89% of participants stayed through the program, and
              every participant showed improvement in their living situation.
            </p>
          </div>
        </div>
      </section>

      {/* SIGNATURE PROGRAMS */}
      <section
        id="signature-programs"
        data-testid="programs-section"
        className="tmg-section"
      >
        <div className="tmg-container">
          <div className="max-w-3xl mb-16">
            <p className="tmg-overline mb-5">— Signature Programs</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
              Programs we deliver with you.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-[var(--tmg-line)] border border-[var(--tmg-line)] mb-12">
            {SIGNATURE_PROGRAMS.map((p, i) => (
              <div
                key={p.name}
                data-testid={`program-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="bg-white p-8 sm:p-10 hover:bg-[var(--tmg-ivory)] transition-all duration-300"
              >
                <span className="font-mono-sm text-xs text-[var(--tmg-gold)]">
                  0{i + 1}
                </span>
                <h3 className="font-display text-2xl font-semibold mt-6 mb-4 leading-tight">
                  {p.name}
                </h3>
                <p className="text-sm text-[var(--tmg-muted)] leading-relaxed mb-6">
                  {p.description}
                </p>
                <a
                  href={p.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`download-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="inline-flex items-center gap-2 text-xs font-semibold font-mono-sm uppercase tracking-[0.14em] text-[var(--tmg-gold-soft)] border-b border-[var(--tmg-gold)] pb-1 hover:text-[var(--tmg-obsidian)] transition-colors"
                >
                  Download one-pager <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </div>
            ))}
          </div>
          <Link
            to="/offerings#training"
            data-testid="programs-cta-btn"
            className="tmg-btn-primary"
          >
            Explore training &amp; programs <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section
        data-testid="faq-section"
        className="tmg-section bg-[var(--tmg-ivory-2)]/40 border-y border-[var(--tmg-line)]"
      >
        <div className="tmg-container grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="tmg-overline mb-5">— Questions</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
              Common questions.
            </h2>
          </div>
          <div className="lg:col-span-8 lg:pl-12 lg:border-l border-[var(--tmg-line)]">
            <Accordion type="single" collapsible>
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`faq-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="border-[var(--tmg-line)]"
                >
                  <AccordionTrigger
                    data-testid={`faq-question-${i}`}
                    className="font-display text-lg sm:text-xl font-semibold hover:no-underline hover:text-[var(--tmg-gold-soft)] py-5"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`faq-answer-${i}`}
                    className="text-base text-[var(--tmg-muted)] leading-relaxed pb-6"
                  >
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* AI READINESS CHECKLIST PROMO */}
      <section data-testid="readiness-promo-section" className="relative tmg-section overflow-hidden bg-[var(--tmg-obsidian)] text-white">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(circle at 15% 30%, rgba(212,175,55,0.22), transparent 45%), radial-gradient(circle at 85% 70%, rgba(212,175,55,0.10), transparent 45%)",
          }}
        />
        <div className="relative tmg-container grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="tmg-overline-gold mb-6 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> FREE TOOL · 3-MIN ASSESSMENT
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] font-semibold leading-[1.05] tracking-tight mb-8">
              Take the{" "}
              <span className="italic font-light text-[var(--tmg-gold-soft)]">
                AI Readiness
              </span>{" "}
              Checklist.
            </h2>
            <p className="text-base sm:text-lg text-white/75 leading-relaxed max-w-xl mb-10">
              16 quick questions. Personalized score. A tailored next-step
              recommendation — plus a branded PDF report delivered instantly.
              Built for small business owners who want clarity, not more tools.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/ai-readiness"
                data-testid="home-readiness-cta"
                className="tmg-btn-primary"
              >
                Start the Checklist <ArrowUpRight className="w-4 h-4" />
              </Link>
              <span className="self-center text-xs text-white/50 font-mono-sm uppercase tracking-[0.18em]">
                Free · No credit card
              </span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div aria-hidden className="absolute -inset-3 border border-[var(--tmg-gold)]/40" />
              <div className="relative bg-white/[0.04] border border-white/10 backdrop-blur-sm p-8 sm:p-10">
                <div className="flex items-end gap-4 mb-6">
                  <div className="font-display text-6xl sm:text-7xl font-semibold text-[var(--tmg-gold)] leading-none">
                    72%
                  </div>
                  <div className="pb-2">
                    <p className="tmg-overline-gold mb-1">— Sample Tier</p>
                    <p className="font-display text-xl font-semibold">Build Ready</p>
                  </div>
                </div>
                <div className="h-px bg-white/10 mb-6" />
                <div className="space-y-3">
                  {[
                    ["AI Awareness", 100],
                    ["Workflow Clarity", 75],
                    ["Tools & Stack", 66],
                    ["Automation Readiness", 100],
                    ["Strategic Foundation", 66],
                  ].map(([k, pct]) => (
                    <div key={k}>
                      <div className="flex justify-between items-baseline text-xs mb-1">
                        <span className="text-white/75">{k}</span>
                        <span className="font-mono-sm text-[var(--tmg-gold)]">{pct}%</span>
                      </div>
                      <div className="h-1 bg-white/10 overflow-hidden">
                        <div
                          className="h-full bg-[var(--tmg-gold)]"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SIGNATURE AUDIT */}
      <section
        data-testid="audit-section"
        className="relative tmg-section overflow-hidden"
      >
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-br from-[var(--tmg-ivory-2)] to-[var(--tmg-ivory)]"
        />
        <div className="relative tmg-container grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="tmg-overline-gold mb-5">— The Signature Starting Point</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-8">
              Not sure what you need first? Start with an{" "}
              <span className="italic font-light">AI & Business Systems Audit.</span>
            </h2>
            <p className="text-base sm:text-lg text-[var(--tmg-muted)] leading-relaxed mb-8">
              Most business owners do not need another random tool. They need clarity.
              The audit is designed to help you understand where your business is now,
              what is slowing you down, and what would make the biggest difference.
            </p>
            <ul className="grid sm:grid-cols-2 gap-3 mb-10">
              {[
                "Business workflow review",
                "AI opportunity review",
                "Software & tool review",
                "Automation recommendations",
                "Business systems roadmap",
                "Suggested next steps",
                "Custom summary",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm">
                  <Check className="w-4 h-4 mt-1 text-[var(--tmg-gold)] flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Link to="/book-call" data-testid="audit-cta-btn" className="tmg-btn-primary">
              Start With an Audit <ArrowUpRight className="w-4 h-4" />
            </Link>
            <div className="mt-6">
              <Link
                to="/realitycheck"
                data-testid="reality-check-btn"
                onClick={() => trackEvent("reality_check_clicked", { meta: { source: "homepage_audit_section" } })}
                className="tmg-btn-secondary"
              >
                Take the free CEO Reality Check <ArrowUpRight className="w-4 h-4" />
              </Link>
              <p className="mt-3 text-xs font-mono-sm uppercase tracking-[0.15em] text-[var(--tmg-muted)]">
                10 minutes. See what you're carrying and get your 90-day plan.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative bg-[var(--tmg-obsidian)] text-white p-10 sm:p-12 shadow-[0_30px_60px_-30px_rgba(212,175,55,0.3)]">
              <div
                aria-hidden
                className="absolute -top-4 -left-4 w-20 h-20 border-l-2 border-t-2 border-[var(--tmg-gold)]"
              />
              <div
                aria-hidden
                className="absolute -bottom-4 -right-4 w-20 h-20 border-r-2 border-b-2 border-[var(--tmg-gold)]"
              />
              <p className="tmg-overline-gold mb-4">— Signature Audit</p>
              <div className="font-display text-6xl sm:text-7xl font-semibold leading-none mb-2">
                $297
              </div>
              <p className="text-white/60 text-sm font-mono-sm mb-8">
                Starting price · 60–90 min
              </p>
              <div className="h-px bg-white/10 mb-8" />
              <p className="text-sm text-white/70 leading-relaxed mb-2">
                A one-session strategy intensive built for owners who want clarity,
                not more tools. Walk away with a roadmap, not a to-do list.
              </p>
              <p className="mt-8 text-[var(--tmg-gold)] text-xs font-mono-sm uppercase tracking-[0.2em]">
                Most popular entry point
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section data-testid="packages-section" className="tmg-section bg-[var(--tmg-ivory-2)]/40 border-y border-[var(--tmg-line)]">
        <div className="tmg-container">
          <div className="max-w-3xl mb-16">
            <p className="tmg-overline mb-5">— Packages</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
              Choose the support that fits your stage.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PACKAGES.map((pkg) => (
              <div
                key={pkg.title}
                data-testid={`package-${pkg.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`p-8 sm:p-10 flex flex-col ${
                  pkg.featured
                    ? "bg-[var(--tmg-obsidian)] text-white ring-1 ring-[var(--tmg-gold)]/60 relative"
                    : "bg-white border border-[var(--tmg-line)]"
                }`}
              >
                {pkg.featured && (
                  <span className="absolute -top-3 left-8 bg-[var(--tmg-gold)] text-[var(--tmg-obsidian)] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] font-mono-sm">
                    Recommended
                  </span>
                )}
                <h3 className="font-display text-2xl font-semibold leading-tight mb-3">
                  {pkg.title}
                </h3>
                <p className={`text-sm mb-6 ${pkg.featured ? "text-[var(--tmg-gold)]" : "text-[var(--tmg-gold-soft)]"} font-mono-sm`}>
                  {pkg.price}
                </p>
                <p className={`text-sm mb-6 ${pkg.featured ? "text-white/70" : "text-[var(--tmg-muted)]"}`}>
                  <span className="font-semibold">Best for: </span>
                  {pkg.bestFor}
                </p>
                <ul className="space-y-2 mb-8 flex-1">
                  {pkg.includes.map((inc) => (
                    <li key={inc} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${pkg.featured ? "text-[var(--tmg-gold)]" : "text-[var(--tmg-gold-soft)]"}`} />
                      <span className={pkg.featured ? "text-white/85" : "text-[var(--tmg-obsidian)]"}>{inc}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/book-call"
                  className={pkg.featured ? "tmg-btn-primary justify-center" : "tmg-btn-secondary justify-center"}
                >
                  {pkg.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section data-testid="about-section" className="tmg-section">
        <div className="tmg-container grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] max-w-md">
              <div aria-hidden className="absolute -inset-3 border border-[var(--tmg-obsidian)]" />
              <img
                src={TABLET_IMAGE}
                alt="TMG Consulting Group"
                className="relative w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7 lg:pt-12">
            <p className="tmg-overline mb-5">— Who We Are</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-8">
              TMG Consulting Group
            </h2>
            <div className="space-y-5 text-base sm:text-lg text-[var(--tmg-muted)] leading-relaxed">
              <p>
                TMG Consulting Group is a Michigan-based consulting firm working
                where leadership, people, and operations meet. Our principal
                consultant has led poverty-alleviation and workforce programs,
                built the Rules of Money curriculum, and designed operating
                systems for small businesses and nonprofits.
              </p>
              <p className="text-[var(--tmg-obsidian)] italic">
                Every engagement is led by our principal consultant, with
                specialist partners brought in when a project calls for it.
              </p>
            </div>
            <div className="mt-10">
              <Link to="/about" data-testid="about-learn-more-btn" className="tmg-btn-secondary">
                Learn More About Us <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* POWERED BY OPTURNA */}
      <section
        data-testid="opturna-section"
        className="relative bg-[var(--tmg-obsidian)] text-white overflow-hidden tmg-grain"
      >
        <img
          src={ABSTRACT_TECH}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none"
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--tmg-obsidian)] via-[var(--tmg-obsidian)]/85 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 py-24 sm:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="tmg-overline-gold mb-6">— Powered by</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] mb-8">
              Opturna.
            </h2>
            <p className="text-base sm:text-lg text-white/75 leading-relaxed max-w-2xl mb-10">
              For clients who need deeper implementation, custom automations,
              workflow buildouts, AI systems, or app development, we
              connect strategy with execution through Opturna — an AI systems
              and automation company focused on helping service-based businesses
              and organizations build practical, tailored systems that work.
            </p>
            <Link
              to="/offerings#ai"
              data-testid="opturna-cta-btn"
              className="tmg-btn-primary"
            >
              Learn About Opturna <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <div className="border border-[var(--tmg-gold)]/30 p-10 backdrop-blur-sm bg-white/[0.02]">
              <p className="tmg-overline-gold mb-6">— Capability Stack</p>
              <ul className="space-y-4">
                {[
                  "Custom AI System Implementation",
                  "Workflow & Automation Buildouts",
                  "No-code App Development",
                  "Internal Dashboards & Portals",
                  "AI-Assisted Operations",
                ].map((c) => (
                  <li key={c} className="flex items-center gap-3 text-sm text-white/80">
                    <span className="w-1.5 h-1.5 bg-[var(--tmg-gold)]" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PROOF / TESTIMONIALS */}
      <section data-testid="proof-section" className="tmg-section">
        <div className="tmg-container">
          <div className="max-w-3xl mb-16">
            <p className="tmg-overline mb-5">— Proof</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
              Experience that blends{" "}
              <span className="italic font-light text-[var(--tmg-gold-soft)]">strategy</span>,
              systems, and real-world implementation.
            </h2>
          </div>

          <div className="grid lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-5 space-y-3 text-sm text-[var(--tmg-muted)]">
              {[
                "AI workshops and beginner-friendly trainings",
                "Workflow and dashboard creation for nonprofit programs",
                "Business coaching and consulting experience",
                "Systems development for entrepreneurs and organizations",
                "Social media and operational support for growing brands",
                "Custom workflows, SOPs, and automation planning",
                "App and dashboard buildouts through Opturna",
              ].map((p) => (
                <div key={p} className="flex items-start gap-3 border-b border-[var(--tmg-line)] pb-3">
                  <span className="w-1 h-1 mt-2.5 bg-[var(--tmg-gold)]" />
                  <span>{p}</span>
                </div>
              ))}
            </div>

            <div className="lg:col-span-7 grid gap-6">
              {TESTIMONIALS.map((t, i) => (
                <figure
                  key={i}
                  data-testid={`testimonial-${i}`}
                  className="border border-[var(--tmg-line)] bg-white p-8 sm:p-10 relative"
                >
                  <span className="absolute top-6 left-8 font-display text-7xl text-[var(--tmg-gold)] leading-none">
                    “
                  </span>
                  <blockquote className="pt-8 text-base sm:text-lg font-display italic font-light leading-snug text-[var(--tmg-obsidian)]">
                    {t.quote}
                  </blockquote>
                  <figcaption className="mt-6 tmg-overline">— {t.author}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
